(function () {
  window.dataLayer = window.dataLayer || [];

  const state = window.GMAnalyticsData || {};
  const pushedKeys = new Set();

  function push(payload) {
    window.dataLayer.push(payload);
  }

  function resetEcommerce() {
    push({ ecommerce: null });
  }

  function normalizePath(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return String(url || '').split('?')[0];
    }
  }

  function itemId(item) {
    const key = state.config?.ga?.productIdentifier || 'sku';
    return item?.[key] || item?.sku || item?.variant_id || item?.product_id;
  }

  function ga4Item(item, overrides) {
    if (!item) return null;

    return {
      item_id: itemId(item),
      item_variant_id: item.variant_id,
      item_product_id: item.product_id,
      item_name: item.name || item.title,
      item_list_name: item.list,
      item_list_id: item.list,
      item_brand: item.brand || item.vendor,
      item_category: item.category,
      item_variant: item.variant_title || item.variant,
      index: item.position,
      quantity: item.quantity,
      price: Number(item.price || 0),
      ...overrides,
    };
  }

  function classicProduct(item, overrides) {
    if (!item) return null;

    return {
      sku: item.sku || item.variant_id,
      variant_id: item.variant_id,
      product_id: item.product_id,
      name: item.name || item.title,
      title: item.title || item.name,
      brand: item.brand || item.vendor,
      vendor: item.vendor || item.brand,
      category: item.category,
      variant: item.variant || item.variant_title,
      variant_title: item.variant_title || item.variant,
      quantity: item.quantity,
      price: Number(item.price || 0),
      position: item.position,
      list: item.list,
      url: item.url,
      ...overrides,
    };
  }

  function compactProduct(product) {
    return Object.fromEntries(Object.entries(product || {}).filter(([, value]) => value !== undefined && value !== null));
  }

  function pageContext(extra) {
    return {
      page_type: state.page?.type,
      page_path: state.page?.path || window.location.pathname,
      ...extra,
    };
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function linkText(link) {
    return cleanText(link?.dataset?.gmAnalyticsLabel || link?.textContent || link?.getAttribute('aria-label'));
  }

  function absoluteUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return url;
    }
  }

  function ecommerceEvent(event, ecommerce, extra) {
    resetEcommerce();
    push({
      event,
      ecommerce,
      ...extra,
    });
  }

  function pushInit() {
    const config = {
      ga: {
        id: state.config?.ga?.id || undefined,
        productIdentifier: state.config?.ga?.productIdentifier || 'sku',
      },
    };

    push({
      event: 'init_global_marketing_variable',
      config,
      ecommerce: {
        currencyCode: state.config?.currencyCode,
      },
      user_properties: {
        visitor_type: state.config?.visitorType || 'guest',
      },
      marketing: {
        user_id: state.config?.userId || undefined,
      },
    });
  }

  function pushViewItem() {
    if (state.page?.type !== 'product' || !state.product) return;
    const product = compactProduct(classicProduct(state.product));

    ecommerceEvent('dl_ga4_view_item', {
      currencyCode: state.config?.currencyCode,
      detail: {
        products: [product],
      },
    });
  }

  function pushViewItemList() {
    const items = state.list?.items || [];
    if (!items.length) return;

    ecommerceEvent('dl_ga4_view_item_list', {
      currencyCode: state.config?.currencyCode,
      item_list_name: state.list?.name || state.page?.path,
      item_list_id: state.list?.id || state.page?.path,
      impressions: items.map((item) =>
        compactProduct(
          classicProduct(item, {
            list: state.list?.name || item.list || state.page?.path,
          })
        )
      ),
    });
  }

  function pushSearch() {
    if (!state.page?.searchTerm) return;

    push({
      event: 'dl_ga4_search',
      search_term: state.page.searchTerm,
    });
  }

  function pushViewCart() {
    if (state.page?.type !== 'cart') return;
    const items = state.cart?.items || [];

    ecommerceEvent(
      'dl_ga4_view_cart',
      {
        currencyCode: state.config?.currencyCode,
        impressions: items.map((item) => compactProduct(classicProduct(item))),
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function pushSelectItem(item) {
    if (!item) return;

    ecommerceEvent('dl_select_item', {
      currencyCode: state.config?.currencyCode,
      item_list_name: state.list?.name || item.list || state.page?.path,
      item_list_id: state.list?.id || item.list || state.page?.path,
      click: {
        products: [
          compactProduct(
            classicProduct(item, {
              list: state.list?.name || item.list || state.page?.path,
            })
          ),
        ],
      },
    });
  }

  function pushAddToCart(item, quantity) {
    if (!item) return;
    const addItem = compactProduct(classicProduct(item, { quantity: Number(quantity || item.quantity || 1) }));
    const key = `add:${addItem.variant_id}:${addItem.quantity}:${Date.now().toString().slice(0, -3)}`;
    if (pushedKeys.has(key)) return;
    pushedKeys.add(key);

    ecommerceEvent(
      'dl_ga4_add_to_cart',
      {
        currencyCode: state.config?.currencyCode,
        add: {
          products: [addItem],
        },
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function pushRemoveFromCart(item, quantity) {
    if (!item) return;
    const removeItem = compactProduct(classicProduct(item, { quantity: Number(quantity || item.quantity || 1) }));

    ecommerceEvent(
      'dl_ga4_remove_from_cart',
      {
        currencyCode: state.config?.currencyCode,
        remove: {
          products: [removeItem],
        },
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function pushBeginCheckout() {
    const items = state.cart?.items || [];
    if (!items.length) return;

    ecommerceEvent(
      'dl_begin_checkout',
      {
        currencyCode: state.config?.currencyCode,
        checkout: {
          products: items.map((item) => compactProduct(classicProduct(item))),
        },
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function itemFromVariantId(variantId) {
    const id = String(variantId || '');
    return state.productVariants?.[id] || state.cart?.items?.find((item) => String(item.variant_id) === id);
  }

  function itemFromCartLine(line) {
    const index = Number(line || 0) - 1;
    return state.cart?.items?.[index];
  }

  function selectedItemFromLink(link) {
    const items = state.list?.items || [];
    const targetPath = normalizePath(link.href);
    return (
      itemFromProductLink(link) ||
      items.find((item) => normalizePath(item.url) === targetPath || targetPath.includes(normalizePath(item.url)))
    );
  }

  function itemFromProductLink(link) {
    if (!link?.dataset?.gmProductId && !link?.dataset?.gmProductVariantId) return null;

    return compactProduct({
      sku: link.dataset.gmProductSku,
      variant_id: link.dataset.gmProductVariantId,
      product_id: link.dataset.gmProductId,
      name: link.dataset.gmProductTitle,
      title: link.dataset.gmProductTitle,
      brand: link.dataset.gmProductVendor,
      vendor: link.dataset.gmProductVendor,
      category: link.dataset.gmProductType,
      variant: link.dataset.gmProductVariant,
      variant_title: link.dataset.gmProductVariant,
      quantity: 1,
      price: Number(link.dataset.gmProductPrice || 0),
      position: Number(link.dataset.gmProductPosition || 0),
      list: link.dataset.gmProductList,
      url: link.href,
    });
  }

  function itemFromCollectionFeatureLink(link) {
    if (!link) return null;

    return compactProduct({
      sku: link.dataset.gmProductSku,
      variant_id: link.dataset.gmProductVariantId,
      product_id: link.dataset.gmProductId,
      name: link.dataset.gmProductTitle,
      title: link.dataset.gmProductTitle,
      brand: link.dataset.gmProductVendor,
      vendor: link.dataset.gmProductVendor,
      category: link.dataset.gmProductType,
      variant: link.dataset.gmProductVariant,
      variant_title: link.dataset.gmProductVariant,
      quantity: 1,
      price: Number(link.dataset.gmProductPrice || 0),
      position: Number(link.dataset.gmProductPosition || 0),
      list: link.dataset.gmCollectionTitle,
      url: link.href,
    });
  }

  function pushRecommendedProductClick(link) {
    const root = link?.closest('product-recommendations');
    const item = itemFromProductLink(link);
    if (!root || !item) return false;

    const listName = root.querySelector('.related-products__heading')?.textContent?.trim() || 'Productos relacionados';
    const product = compactProduct(classicProduct(item, { list: listName }));

    ecommerceEvent(
      'gm_recommended_product_click',
      {
        currencyCode: state.config?.currencyCode,
        item_list_name: listName,
        item_list_id: root.dataset.sectionId || 'related-products',
        click: {
          products: [product],
        },
      },
      pageContext({
        source: 'recommended_products',
        section_id: root.dataset.sectionId,
        product_url: item.url,
        product,
      })
    );

    return true;
  }

  function pushCollectionFeatureClick(link) {
    const root = link?.closest('[data-gm-collection-feature]');
    const item = itemFromCollectionFeatureLink(link);
    if (!root || !item) return;

    const source = root.dataset.gmCollectionFeatureSource || state.page?.type || 'unknown';
    const collectionHandle = link.dataset.gmCollectionHandle || root.dataset.gmCollectionFeatureCollection;
    const collectionTitle = link.dataset.gmCollectionTitle || collectionHandle;
    const listName = `GM Collection Feature - ${collectionTitle}`;
    const product = compactProduct(
      classicProduct(item, {
        list: listName,
      })
    );

    ecommerceEvent(
      'gm_collection_feature_click',
      {
        currencyCode: state.config?.currencyCode,
        item_list_name: listName,
        item_list_id: collectionHandle,
        click: {
          products: [product],
        },
      },
      {
        source,
        page_type: state.page?.type,
        page_path: state.page?.path,
        section_id: root.dataset.gmCollectionFeatureSectionId,
        collection_handle: collectionHandle,
        collection_title: collectionTitle,
        product_url: item.url,
        product,
        collection_feature: {
          source,
          page_type: state.page?.type,
          page_path: state.page?.path,
          section_id: root.dataset.gmCollectionFeatureSectionId,
          collection_handle: collectionHandle,
          collection_title: collectionTitle,
          product_url: item.url,
          product,
        },
      }
    );
  }

  function pushMenuClick(link) {
    const header = link?.closest('header, header-drawer, .shopify-section-header');
    if (!header) return;

    push(
      pageContext({
        event: 'gm_menu_click',
        source: link.closest('header-drawer, .menu-drawer') ? 'mobile_menu' : 'main_menu',
        menu_text: linkText(link),
        menu_url: absoluteUrl(link.href),
        menu_path: normalizePath(link.href),
      })
    );
  }

  function pushMenuOpen(summary) {
    if (!summary?.closest('header, header-drawer, .shopify-section-header')) return;

    push(
      pageContext({
        event: 'gm_menu_open',
        source: summary.closest('header-drawer, .menu-drawer') ? 'mobile_menu' : 'main_menu',
        menu_text: linkText(summary),
      })
    );
  }

  function pushGarageEvent(eventName, extra) {
    push(
      pageContext({
        event: eventName,
        source: 'garage',
        ...extra,
      })
    );
  }

  function pushPredictiveSearchClick(link) {
    const root = link?.closest('predictive-search');
    if (!root) return false;

    const input = root.querySelector('input[type="search"], input[name="q"]');
    const product = itemFromProductLink(link);
    push(
      pageContext({
        event: 'gm_predictive_search_click',
        source: 'predictive_search',
        search_term: input?.value || '',
        result_text: linkText(link),
        result_url: absoluteUrl(link.href),
        result_path: normalizePath(link.href),
        result_type: link.href.includes('/products/') ? 'product' : link.href.includes('/collections/') ? 'collection' : 'suggestion',
        product,
      })
    );

    return true;
  }

  function pushSearchSubmit(form) {
    const input = form?.querySelector('input[type="search"], input[name="q"]');
    const searchTerm = cleanText(input?.value);
    if (!searchTerm) return;

    push(
      pageContext({
        event: 'gm_search_submit',
        source: form.closest('predictive-search') ? 'predictive_search' : 'search_form',
        search_term: searchTerm,
      })
    );
  }

  function pushWhatsappClick(link) {
    const product = state.product ? compactProduct(classicProduct(state.product)) : undefined;

    push(
      pageContext({
        event: 'gm_whatsapp_click',
        source: link.closest('.gm-sales-product') ? 'pdp' : 'site',
        link_text: linkText(link),
        link_url: absoluteUrl(link.href),
        whatsapp_phone: link.href.match(/(?:wa\.me\/|phone=)(\d+)/)?.[1],
        product_url: product?.url,
        product,
      })
    );
  }

  function parseRequestBody(body) {
    if (!body) return {};
    if (body instanceof FormData) return Object.fromEntries(body.entries());
    if (body instanceof URLSearchParams) return Object.fromEntries(body.entries());
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (error) {
        return Object.fromEntries(new URLSearchParams(body).entries());
      }
    }
    if (typeof body === 'object') return body;
    return {};
  }

  function captureFetchEvents() {
    const originalFetch = window.fetch;
    if (!originalFetch) return;

    window.fetch = function (...args) {
      const request = args[0];
      const init = args[1] || {};
      const url = typeof request === 'string' ? request : request?.url;
      const body = init.body || request?.body;
      const bodyData = parseRequestBody(body);

      return originalFetch.apply(this, args).then((response) => {
        const path = normalizePath(url);

        if (response.ok && path.includes('/cart/add')) {
          response
            .clone()
            .json()
            .then((payload) => {
              const items = payload.items || [payload];
              items.forEach((line) => {
                const item = itemFromVariantId(line.id) || itemFromVariantId(line.variant_id);
                pushAddToCart(item || line, line.quantity || bodyData.quantity || 1);
              });
            })
            .catch(() => {
              pushAddToCart(itemFromVariantId(bodyData.id), bodyData.quantity || 1);
            });
        }

        if (response.ok && path.includes('/cart/change')) {
          const quantity = Number(bodyData.quantity);
          if (quantity === 0) {
            pushRemoveFromCart(itemFromVariantId(bodyData.id) || itemFromCartLine(bodyData.line), 1);
          }
        }

        return response;
      });
    };
  }

  function bindClickEvents() {
    document.addEventListener(
      'click',
      (event) => {
        const productLink = event.target.closest('a[href*="/products/"]');
        const predictiveLink = event.target.closest('predictive-search a[href]');
        if (predictiveLink && pushPredictiveSearchClick(predictiveLink)) return;

        if (productLink) {
          const collectionFeatureLink = productLink.closest('[data-gm-collection-feature-product]');
          if (collectionFeatureLink) {
            pushCollectionFeatureClick(collectionFeatureLink);
            return;
          }

          if (pushRecommendedProductClick(productLink)) return;

          pushSelectItem(selectedItemFromLink(productLink));
        }

        const checkoutButton = event.target.closest('button[name="checkout"], a[href*="/checkout"]');
        if (checkoutButton) {
          pushBeginCheckout();
        }

        const whatsappLink = event.target.closest('a[href*="wa.me/"], a[href*="api.whatsapp.com"], a[href*="whatsapp.com/send"]');
        if (whatsappLink) {
          pushWhatsappClick(whatsappLink);
        }

        const garageOpen = event.target.closest('[data-gm-garage-open]');
        if (garageOpen) {
          pushGarageEvent('gm_garage_open', {
            trigger_text: linkText(garageOpen),
          });
        }

        const garageClear = event.target.closest('[data-gm-garage-clear]');
        if (garageClear) {
          pushGarageEvent('gm_garage_clear');
        }

        const businessLink = event.target.closest('.gm-business-banner__link[href]');
        if (businessLink) {
          push(
            pageContext({
              event: 'gm_business_banner_click',
              source: 'header_business_banner',
              link_text: linkText(businessLink),
              link_url: absoluteUrl(businessLink.href),
            })
          );
        }

        const menuSummary = event.target.closest('summary.header__menu-item, summary.menu-drawer__menu-item');
        if (menuSummary) {
          pushMenuOpen(menuSummary);
        }

        const menuLink = event.target.closest('a.header__menu-item[href], a.mega-menu__link[href], a.menu-drawer__menu-item[href]');
        if (menuLink) {
          pushMenuClick(menuLink);
        }
      },
      true
    );
  }

  function bindSubmitFallback() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (form?.action && normalizePath(form.action).includes('/search')) {
          pushSearchSubmit(form);
        }

        if (!form?.action || !normalizePath(form.action).includes('/cart/add')) return;

        const formData = new FormData(form);
        pushAddToCart(itemFromVariantId(formData.get('id')), formData.get('quantity') || 1);
      },
      true
    );
  }

  function bindGarageEvents() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form?.matches?.('[data-gm-garage-form]')) return;

        const formData = new FormData(form);
        pushGarageEvent('gm_garage_save', {
          vehicle_brand: formData.get('brand'),
          vehicle_model: formData.get('model'),
          vehicle_year: formData.get('year'),
          vehicle_version: formData.get('subModel'),
        });
      },
      true
    );
  }

  pushInit();
  pushViewItem();
  pushViewItemList();
  pushSearch();
  pushViewCart();
  captureFetchEvents();
  bindClickEvents();
  bindSubmitFallback();
  bindGarageEvents();
})();
